import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, FileText, Clock, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';
import { canEdit } from '@/lib/permissions';

const QuotationDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [quotation, setQuotation] = useState<any>(null);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchQuotation();
    }
  }, [id]);

  const fetchQuotation = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getSupplierQuotation(businessId, parseInt(id!));
      
      if (response.data?.success) {
        setQuotation(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load quotation details",
        });
        navigate('/procurement/quotations');
      }
    } catch (error: any) {
      console.error('Error fetching quotation:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load quotation details",
      });
      navigate('/procurement/quotations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      const response = await ProcurementService.updateQuotationStatus(businessId, parseInt(id!), newStatus);
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: `Quotation ${newStatus} successfully`,
        });
        fetchQuotation();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to update quotation status",
        });
      }
    } catch (error: any) {
      console.error('Error updating quotation status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update quotation status",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { variant: 'secondary' as const, label: 'Draft' },
      sent: { variant: 'outline' as const, label: 'Sent' },
      received: { variant: 'default' as const, label: 'Received' },
      approved: { variant: 'default' as const, label: 'Approved' },
      rejected: { variant: 'destructive' as const, label: 'Rejected' },
      expired: { variant: 'secondary' as const, label: 'Expired' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading quotation details...</span>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Quotation Not Found</h3>
          <p className="text-muted-foreground">The requested quotation could not be found.</p>
          <Button 
            onClick={() => navigate('/procurement/quotations')}
            className="mt-4"
          >
            Back to Quotations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/procurement/quotations')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quotations
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {canEdit('quotations') && quotation.status === 'received' && (
            <>
              <Button
                onClick={() => handleStatusUpdate('approved')}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleStatusUpdate('rejected')}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold tracking-tight">Quotation #{quotation.quotationNumber}</h1>
          {getStatusBadge(quotation.status)}
        </div>
        <p className="text-muted-foreground">
          From {quotation.supplier?.name} • Requested on {new Date(quotation.requestDate).toLocaleDateString()}
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Quotation Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Quotation Number</label>
                  <p className="text-sm">{quotation.quotationNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Request Date</label>
                  <p className="text-sm">{new Date(quotation.requestDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Due Date</label>
                  <p className="text-sm">
                    {quotation.dueDate ? new Date(quotation.dueDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Total Amount</label>
                  <p className="text-lg font-semibold">
                    {quotation.totalAmount ? formatCurrency(quotation.totalAmount) : 'Pending'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Supplier Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Supplier Name</label>
                  <p className="text-sm">{quotation.supplier?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Contact Email</label>
                  <p className="text-sm">{quotation.supplier?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Phone</label>
                  <p className="text-sm">{quotation.supplier?.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Address</label>
                  <p className="text-sm">{quotation.supplier?.address || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quoted Items</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotation.items?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.itemName}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unitPrice || 0)}</TableCell>
                      <TableCell>{formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="terms" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium">Payment Terms</h4>
                  <p className="text-sm text-muted-foreground">
                    {quotation.paymentTerms || 'Net 30 days'}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">Delivery Terms</h4>
                  <p className="text-sm text-muted-foreground">
                    {quotation.deliveryTerms || 'Standard delivery'}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">Validity Period</h4>
                  <p className="text-sm text-muted-foreground">
                    {quotation.validityPeriod || '30 days from quotation date'}
                  </p>
                </div>
                {quotation.notes && (
                  <div>
                    <h4 className="font-medium">Additional Notes</h4>
                    <p className="text-sm text-muted-foreground">{quotation.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuotationDetail; 